import React, { useEffect, useState } from "react";
import User from "../components/Utils";
import MenuBar from "../components/Menubar";
import AchievementsModal from "../modals/AchievementsModal";
import AchievementsSectionRow from "../components/AchievementsSectionRow";

type Props = {
  user: User;
  handleLogin(user): void
};

type UiMedal = {
  key: string;
  title: string;
  unlocked: boolean;
  imageKey: string;
  earnedAt?: string | null;
  description?: string | null;
};

export type UiSection = {
  key: "jumpstarter" | "community" | "streak";
  title: string;
  medals: UiMedal[];
};

type ApiMedal = {
  id: number;
  title: string;
  displayOrder: number;
  description: string | null;
  currentStatus: boolean;
  unlockedDate: string | null;
};

type ApiSection = {
  section: "Jumpstarter" | "Supporting the Community" | "Yolomeal Streak";
  totalUnlocked: number;
  medals: ApiMedal[];
};

const sectionKeyByTitle: Record<ApiSection["section"], UiSection["key"]> = {
  "Jumpstarter": "jumpstarter",
  "Supporting the Community": "community",
  "Yolomeal Streak": "streak",
};

function getImageCode(section: string, displayOrder: number) {
  if (section === "Jumpstarter") return `J${displayOrder}`;
  if (section === "Supporting the Community") return `C${displayOrder}`;
  if (section === "Yolomeal Streak") return `S${displayOrder}`;
  return "";
}

function mapApiToUi(api: ApiSection[]): UiSection[] {
  return api.map((s) => {
    const key = sectionKeyByTitle[s.section];
    const medalsSorted = [...s.medals].sort((a, b) => a.displayOrder - b.displayOrder);
    const medals: UiSection["medals"] = medalsSorted.map((m) => ({
      key: `medal_${m.id}`,
      title: m.title,
      unlocked: !!m.currentStatus,
      imageKey: getImageCode(s.section, m.displayOrder),
      earnedAt: m.unlockedDate,
      description: m.description,
    }));
    return { key, title: s.section, medals };
  });
}

const AchievementsPage: React.FC<Props> = ({ user }) => {
  const [sections, setSections] = useState<UiSection[]>([]);
  const [openSection, setOpenSection] = useState<UiSection | null>(null);

  useEffect(() => {
    if (!user || !(user as any).id) return;
    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/achievement/page/${(user as any).id}`, {
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Accept: "application/json" ,
            Authorization: 'Bearer ' + user.token,
          },
        });
        if (!res.ok)
          throw new Error(`HTTP ${res.status}`);
        const data: ApiSection[] = await res.json();
        setSections(mapApiToUi(data));
      } catch {
        setSections([]);
      }
    })();

    return () => controller.abort();
  }, [user]);

  return (
    <main className="ym-achievements">
      <div className="container">
        <MenuBar user={user} />
        <h1 className="ym-page-title">Achievements</h1>

        {sections.map((s, i) => (
          <AchievementsSectionRow
            key={s.key}
            section={s}
            onViewAll={setOpenSection}
            showViewAll={!(i === sections.length - 1 && s.medals.length <= 5) && s.key !== "community"}
          />
        ))}

        <AchievementsModal
          isOpen={!!openSection}
          onRequestClose={() => setOpenSection(null)}
          title={openSection?.title || ""}
          medals={openSection?.medals || []}
          sectionKey={openSection?.key as "jumpstarter" | "community" | "streak"}
        />
      </div>
    </main>
  );
};

export default AchievementsPage;
